import { FC, ReactNode, CSSProperties, useMemo } from 'react';

import cn from 'classnames';
import { Modal } from '@mui/material';

import { useBridgeModal } from '@src/hooks/useBridgeModal';
import { CgClose } from 'react-icons/cg';
import { IoChevronBack } from 'react-icons/io5';

import './oModal.scss';

export interface IOModalProps extends Omit<React.ComponentProps<typeof Modal>, 'open' | 'onClose' | 'children'> {
  isOpen: boolean;
  keepMounted?: boolean;
  children: ReactNode;
  anchorEl?: HTMLElement | null;
  anchorOffset?: number; // vertical offset in px
  classes?: {
    modal?: string;
    container?: string;
    body?: string;
    header?: string;
    closeIcon?: string;
    closeIconWrapper?: string;
    title?: string;
    footer?: string;
    backButton?: string;
  };
  titleWrapperAlignment?: 'center' | 'left' | 'right';
  onClose: () => void;
  hasBackdrop?: boolean;
  footer?: ReactNode;
  title?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
}

const OModal: FC<IOModalProps> = ({
  isOpen,
  children,
  classes,
  anchorEl,
  anchorOffset = 8,
  onClose,
  footer,
  hasBackdrop = false,
  keepMounted = false,
  title,
  showBackButton = false,
  onBackClick,
  titleWrapperAlignment = 'center',
  ...rest
}) => {
  useBridgeModal(isOpen);

  const containerStyle: CSSProperties | undefined = useMemo(() => {
    if (!anchorEl) return undefined;
    const rect = anchorEl.getBoundingClientRect();
    const top = rect.bottom + anchorOffset + window.scrollY;
    const left = rect.left + rect.width / 10 + window.scrollX;
    return {
      position: 'fixed',
      top,
      left,
      maxWidth: 'calc(100vw - 24px)',
    };
  }, [anchorEl, anchorOffset]);

  return (
    <Modal
      className={classes?.modal}
      open={!!isOpen}
      onClose={onClose}
      keepMounted={keepMounted}
      hideBackdrop={!hasBackdrop}
      classes={{
        root: 'o-modal-root-container',
      }}
      {...rest}
    >
      <div className={cn('o-modal-container', classes?.container)} id="o-modal-container" style={containerStyle}>
        <div className={cn('modal-header', classes?.header)} id="o-modal-header">
          <div className={cn('modal-title-wrapper', [titleWrapperAlignment])}>
            {showBackButton && (
              <button
                className={cn('modal-back-button', classes?.backButton)}
                onClick={onBackClick}
                id="o-modal-back-button"
              >
                <IoChevronBack size={24} />
              </button>
            )}
            {title && (
              <span className={cn('modal-title', classes?.title)} id="o-modal-title">
                {title}
              </span>
            )}
          </div>

          <button
            className={cn('close-icon-circle', 'icon-container', classes?.closeIconWrapper)}
            onClick={onClose}
            id="o-modal-close-button"
          >
            <CgClose size={15} className={cn('close-icon', classes?.closeIcon)} />
          </button>
        </div>

        <div className={cn('modal-body', classes?.body)} id="o-modal-body">
          {children}
        </div>
        {footer && (
          <div className={cn('modal-footer', classes?.footer ?? '')} id="o-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default OModal;
